import { Controller } from "@hotwired/stimulus"

/**
 * Generic form controller for has_many tax organizer associations
 * Works with: dependents, state_residencies, care_expenses, estimated_payments
 *
 * Usage:
 *   data-controller="tax-organizer--collection-form"
 *   data-tax-organizer--collection-form-resource-name-value="dependent"
 *   data-tax-organizer--collection-form-create-url-value="/path/to/create"
 *   data-tax-organizer--collection-form-update-url-value="/path/:id"
 *   data-tax-organizer--collection-form-destroy-url-value="/path/:id"
 *   data-tax-organizer--collection-form-question-id-value="123"
 *   data-tax-organizer--collection-form-max-items-value="10"
 *   data-tax-organizer--collection-form-additional-params-value='{"key": "value"}' (optional)
 *
 *   Rows:
 *   data-tax-organizer--collection-form-target="row"
 *   data-row-id="123" (for persisted records)
 *
 *   Fields within rows:
 *   data-tax-organizer--collection-form-target="field"
 *
 *   Add button:
 *   data-tax-organizer--collection-form-target="addButton"
 */
export default class extends Controller {
  static targets = ["row", "field", "addButton", "list", "dnaCheckbox"]
  static outlets = ["tax-organizer--scroll-position", "tax-organizer--progress"]

  static values = {
    createUrl: String,
    updateUrl: String,
    destroyUrl: String,
    questionId: String,
    maxItems: Number,
    resourceName: String, // e.g., "dependent", "state_residency", etc.
    additionalParams: Object // Optional: additional params to send with POST (e.g., {payment_type: 'federal'})
  }

  connect() {
    this.savingRows = new Set() // Track which rows are currently saving
    this.lastSavedValues = new Map() // Track last saved values per row
    this.pendingSaves = new Map() // Track pending saves per row (queued during active save)
    this.updateAddButtonState()

    // Bind and register the morph protection handler
    this.boundMorphHandler = this.preventMorphingActiveInput.bind(this)
    document.addEventListener('turbo:before-morph-element', this.boundMorphHandler)
  }

  disconnect() {
    // Clean up the event listener
    document.removeEventListener('turbo:before-morph-element', this.boundMorphHandler)
  }

  // Prevent morphing of the currently focused input to avoid clobbering user input
  preventMorphingActiveInput(event) {
    const activeElement = document.activeElement
    const morphTarget = event.target

    // Only prevent morph if:
    // 1. There's an active element
    // 2. The morph target is the active element
    // 3. The active element is an input within this controller's scope
    // 4. The active element is an actual input/textarea/select
    if (activeElement &&
        activeElement === morphTarget &&
        this.element.contains(activeElement) &&
        (activeElement.tagName === 'INPUT' ||
         activeElement.tagName === 'TEXTAREA' ||
         activeElement.tagName === 'SELECT')) {

      // Sync validation classes from new element before preventing morph
      // This handles the edge case where the user tabs back to an invalid field
      // before the validation response arrives
      const newElement = event.detail.newElement
      if (newElement) {
        if (newElement.classList.contains('is-invalid')) {
          activeElement.classList.add('is-invalid')
        } else {
          activeElement.classList.remove('is-invalid')
        }
      }

      event.preventDefault()
    }
  }

  // Get current values for a specific row
  getCurrentValues(row) {
    const values = {}
    this.fieldTargets.forEach(field => {
      if (row.contains(field)) {
        if (field.type === 'checkbox') {
          values[field.name] = field.checked
        } else if (field.type === 'file') {
          values[field.name] = field.files.length > 0 ? 'has_file' : ''
        } else {
          values[field.name] = field.value.trim()
        }
      }
    })
    return values
  }

  // Check if row has changes since last save
  hasChanges(rowId, currentValues) {
    const lastSaved = this.lastSavedValues.get(rowId)
    if (!lastSaved) return true // No saved values yet, consider it changed

    for (const [name, value] of Object.entries(currentValues)) {
      if (lastSaved[name] !== value) {
        return true
      }
    }
    return false
  }

  // Add a new row by immediately persisting an empty record
  async addRow(event) {
    event.preventDefault()

    // Check if we've hit the max limit
    if (this.hasMaxItemsValue) {
      const visibleCount = this.rowTargets.filter(row => row.style.display !== 'none').length
      if (visibleCount >= this.maxItemsValue) {
        return
      }
    }

    // Remember scroll position
    if (this.hasTaxOrganizerScrollPositionOutlet) {
      this.taxOrganizerScrollPositionOutlet.rememberFromElement(this.element)
    }

    // Show spinner
    if (this.hasQuestionIdValue && this.hasTaxOrganizerProgressOutlet) {
      this.taxOrganizerProgressOutlet.showQuestionSaving(this.questionIdValue)
    }

    try {
      // POST to create empty record
      const formData = new FormData()

      // Send empty params for the resource to satisfy strong params
      if (this.hasResourceNameValue) {
        formData.append(`${this.resourceNameValue}[_placeholder]`, '')
      }

      // Add any additional params (e.g., payment_type for estimated_payments)
      if (this.hasAdditionalParamsValue) {
        Object.entries(this.additionalParamsValue).forEach(([key, value]) => {
          formData.append(`${this.resourceNameValue}[${key}]`, value)
        })
      }

      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: formData
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        this.updateAddButtonState()
      }
    } finally {
      // Hide spinner
      if (this.hasQuestionIdValue && this.hasTaxOrganizerProgressOutlet) {
        this.taxOrganizerProgressOutlet.hideQuestionSaving(this.questionIdValue)
      }
    }
  }

  // Remove a row
  async removeRow(event) {
    event.preventDefault()

    const row = event.currentTarget.closest('[data-tax-organizer--collection-form-target="row"]')
    if (!row) return

    const rowId = row.dataset.rowId
    if (!rowId) return

    if (!confirm('Are you sure you want to remove this item?')) {
      return
    }

    // Remember scroll position
    if (this.hasTaxOrganizerScrollPositionOutlet) {
      this.taxOrganizerScrollPositionOutlet.rememberFromElement(row)
    }

    // Make DELETE request
    const url = this.destroyUrlValue.replace(':id', rowId)
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': this.csrfToken,
        'Accept': 'text/vnd.turbo-stream.html'
      }
    })

    if (response.ok) {
      const html = await response.text()
      Turbo.renderStreamMessage(html)
      this.updateAddButtonState()
    }
  }

  // Update add button state based on current count
  updateAddButtonState() {
    if (!this.hasAddButtonTarget) return

    if (this.hasDnaCheckboxTarget && this.dnaCheckboxTarget.checked) {
      this.addButtonTarget.disabled = true
      this.addButtonTarget.classList.add('disabled')
      return
    }

    const visibleCount = this.rowTargets.filter(row => row.style.display !== 'none').length
    const hasMaxItems = this.hasMaxItemsValue && visibleCount >= this.maxItemsValue

    if (hasMaxItems) {
      this.addButtonTarget.disabled = true
      this.addButtonTarget.classList.add('disabled')
    } else {
      this.addButtonTarget.disabled = false
      this.addButtonTarget.classList.remove('disabled')
    }
  }

  // Update add button enabled state based on whether saves are in progress
  updateAddButtonEnabledState() {
    if (!this.hasAddButtonTarget) return

    // Disable button if any row is currently saving
    const isSaving = this.savingRows.size > 0

    if (isSaving) {
      this.addButtonTarget.disabled = true
      this.addButtonTarget.classList.add('disabled')
      this.element.setAttribute('data-saving', 'true')
    } else {
      // Re-check max items when re-enabling
      this.updateAddButtonState()
      this.element.removeAttribute('data-saving')
    }
  }

  // Auto-save on change
  async autoSave(event) {
    // Find which row this event came from
    const row = this.rowTargets.find(row => row.contains(event.target))
    if (!row) return

    const rowId = row.dataset.rowId
    if (!rowId) return // Shouldn't happen since all rows are persisted now

    // For file inputs, always save (can't reliably detect if a different file was selected)
    const isFileInput = event.target.type === 'file'

    // Get current values for change detection and later saving
    const currentValues = this.getCurrentValues(row)

    // Check if values have changed (skip check for file inputs)
    if (!isFileInput && !this.hasChanges(rowId, currentValues)) {
      return // No changes, skip save
    }

    // If a save is already in progress for this row, queue this save for later
    // This handles rapid successive changes (e.g., selecting state then immediately selecting months)
    if (this.savingRows.has(rowId)) {
      this.pendingSaves.set(rowId, { row, currentValues })
      return
    }

    // Perform the save
    await this.performSave(row, rowId, currentValues)
  }

  // Perform the actual save operation
  async performSave(row, rowId, currentValues) {

    try {
      this.savingRows.add(rowId)
      this.updateAddButtonEnabledState()
      this.dispatch("save-start")

      // Remember scroll position
      if (this.hasTaxOrganizerScrollPositionOutlet) {
        this.taxOrganizerScrollPositionOutlet.rememberFromElement(row)
      }

      // Show spinner on question badge
      if (this.hasQuestionIdValue && this.hasTaxOrganizerProgressOutlet) {
        this.taxOrganizerProgressOutlet.showQuestionSaving(this.questionIdValue)
      }

      // Build form data from all fields in this row
      const formData = new FormData()
      this.fieldTargets.forEach(field => {
        if (row.contains(field)) {
          if (field.type === 'checkbox') {
            formData.append(field.name, field.checked)
          } else if (field.type === 'file') {
            if (field.files.length > 0) {
              formData.append(field.name, field.files[0])
            }
          } else {
            formData.append(field.name, field.value)
          }
        }
      })

      const url = this.updateUrlValue.replace(':id', rowId)
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: formData
      })

      // Manually process Turbo Stream response
      if (response.ok || response.status === 422) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)

        // Update last saved values on both success and 422 to prevent re-submitting same data
        this.lastSavedValues.set(rowId, currentValues)

        // Clear validation errors on successful save (since we skip question frame morph)
        if (response.ok) {
          this.clearValidationErrors(row)
        }
      }

      // Hide spinner on question badge
      if (this.hasQuestionIdValue && this.hasTaxOrganizerProgressOutlet) {
        this.taxOrganizerProgressOutlet.hideQuestionSaving(this.questionIdValue)
      }

      this.savingRows.delete(rowId)
      this.updateAddButtonEnabledState()

      // Check if there's a pending save for this row
      if (this.pendingSaves.has(rowId)) {
        const pending = this.pendingSaves.get(rowId)
        this.pendingSaves.delete(rowId)
        // Trigger the pending save
        this.performSave(pending.row, rowId, pending.currentValues)
      } else if (!this.hasSavesInProgress) {
        this.dispatch("save-complete")
      }
    } catch (error) {
      console.error('Auto-save failed:', error)
      this.savingRows.delete(rowId)
      this.updateAddButtonEnabledState()

      // Hide spinner on error
      if (this.hasQuestionIdValue && this.hasTaxOrganizerProgressOutlet) {
        this.taxOrganizerProgressOutlet.hideQuestionSaving(this.questionIdValue)
      }

      // Clear any pending save on error
      this.pendingSaves.delete(rowId)
      if (!this.hasSavesInProgress) {
        this.dispatch("save-complete")
      }
    }
  }

  // Clear validation errors from a specific row
  clearValidationErrors(row) {
    // Remove is-invalid class from all fields in this row
    this.fieldTargets.forEach(field => {
      if (row.contains(field)) {
        field.classList.remove('is-invalid')
      }
    })

    // Remove all invalid-feedback elements in this row
    row.querySelectorAll('.invalid-feedback').forEach(feedback => {
      feedback.remove()
    })
  }

  // Show file input for re-uploading (used by estimated payments)
  showFileInput(event) {
    event.preventDefault()

    // Find the row containing the button
    const row = event.currentTarget.closest('[data-tax-organizer--collection-form-target="row"]')
    if (!row) return

    // Find the hidden file input within this row
    const fileInput = row.querySelector('input[type="file"].d-none')
    if (!fileInput) return

    // Show the file input
    fileInput.classList.remove('d-none')

    // Hide the success alert with the pencil button
    const alert = row.querySelector('.alert-success')
    if (alert) {
      alert.classList.add('d-none')
    }
  }

  // Helper to get CSRF token
  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  // Check if any saves are currently in progress (used by shareholder upload toggle)
  get hasSavesInProgress() {
    return this.savingRows.size > 0 || this.pendingSaves.size > 0
  }
}
